import {
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Badge,
  Button,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Popover, Whisper } from "rsuite";
import { useMemo, useRef } from "react";

import useEnableDisableAccount from "../../../api/api-hooks/supportal/useEnableDisableAccount";
import DropdownOptionWithIcon from "../../../components/Menus/DropdownOptionWithIcon";
import useImpersonateUser from "../../../api/api-hooks/supportal/useImpersonateUser";
import { usePersonSupportal } from "../../../api/api-hooks/supportal/usePerson";
import PopoverDropdownMenu from "../../../components/Menus/PopoverDropdownMenu";
import useUser from "../../../api/api-hooks/auth/useUser";
import Constants from "../../../utils/Constants";

interface SupportalUserHeaderProps {
  person: any;
  urlStart: string;
}

const SupportalUserHeader = ({
  person,
  urlStart,
}: SupportalUserHeaderProps) => {
  const navigate = useNavigate();
  const whisperRef = useRef<any>(null);
  const { deletePerson } = usePersonSupportal(person?.id);
  const { impersonateUser } = useImpersonateUser();
  const { modifyAccountStatus } = useEnableDisableAccount();
  const { user } = useUser();

  const hasGlobal = useMemo(() => {
    if (!Array.isArray(person?.Tenants)) return false;

    for (const tenant of person.Tenants) {
      if (tenant.TenantID === Constants.SUP_CLOUD_ID) return true;
    }

    return false;
  }, [person?.Tenants]);

  return (
    <div className="header">
      <Container fluid>
        <div className="header-body">
          <Row className="align-items-center py-4">
            <Col lg="auto" md="12">
              <div className="tw:mb-2 tw:flex tw:items-center">
                {person && !person.Name && (
                  <Badge
                    style={{ top: "-2px", position: "relative" }}
                    className="mr-1"
                    color="light"
                  >
                    Pending Invite
                  </Badge>
                )}
                {hasGlobal && (
                  <Badge
                    style={{ top: "-2px", position: "relative" }}
                    className="mr-1"
                    color="info"
                  >
                    Support User
                  </Badge>
                )}
                {!!person?.Disabled && (
                  <Badge color="danger" className="mr-2">
                    Disabled
                  </Badge>
                )}
                <p className="tw:p-0 tw:m-0 tw:font-bold">
                  {person
                    ? person.Name
                      ? person.Name
                      : person.PrimaryContactEmail
                    : "Loading..."}
                </p>

                <Badge
                  color={
                    person?.Status === "Active"
                      ? "success"
                      : person?.Status === "Inactive"
                      ? "danger"
                      : "warning"
                  }
                  style={{ fontSize: "10px" }}
                  className="tw:ml-2"
                >
                  {person?.Status}
                </Badge>
              </div>
              <Breadcrumb
                className="d-none d-md-block mt-1"
                listClassName="breadcrumb-links"
              >
                <BreadcrumbItem>
                  <Link to={urlStart}>
                    <i className="mdi mdi-lifebuoy" />
                  </Link>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <Link to={`${urlStart}`}>Users</Link>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  {person
                    ? person.Name
                      ? person.Name
                      : person.PrimaryContactEmail
                    : ""}
                </BreadcrumbItem>
              </Breadcrumb>
            </Col>
            <Col className="text-lg-right mt-3 mt-lg-0" lg="" md="12">
              <Whisper
                placement="autoVerticalEnd"
                trigger="click"
                ref={whisperRef}
                speaker={
                  <Popover
                    className="sidebar-popover"
                    style={{ width: 225 }}
                    onClick={() => whisperRef?.current?.close()}
                  >
                    <PopoverDropdownMenu>
                      <DropdownOptionWithIcon
                        name="Reset Password"
                        disabled
                        icon={`mdi mdi-refresh text-dark`}
                        onClick={undefined}
                      />

                      <DropdownOptionWithIcon
                        name="Sign In As User"
                        onClick={() => impersonateUser({ user: person })}
                        icon="mdi mdi-account text-dark"
                      />
                      {!!person?.Disabled ? (
                        <DropdownOptionWithIcon
                          name="Enable Account"
                          onClick={() =>
                            modifyAccountStatus({
                              userID: person.id,
                              accountStatus: !person?.Disabled,
                            })
                          }
                          icon="mdi mdi-account-plus"
                        />
                      ) : (
                        <DropdownOptionWithIcon
                          name="Disable Account"
                          onClick={() =>
                            modifyAccountStatus({
                              userID: person.id,
                              accountStatus: !person?.Disabled,
                            })
                          }
                          disabled={person?.id === user?.id}
                          icon="mdi mdi-account-off"
                        />
                      )}
                      <DropdownOptionWithIcon
                        name="Delete User"
                        onClick={() => {
                          deletePerson({
                            currentPerson: person,
                            onSuccess: () => navigate(urlStart),
                          });
                        }}
                        icon={`mdi mdi-delete text-dark`}
                      />
                    </PopoverDropdownMenu>
                  </Popover>
                }
              >
                <Button className="btn-icon btn-outline-secondary">
                  <span className="btn-inner--text">Actions</span>
                  <span className="btn-inner--icon">
                    <i
                      className="mdi mdi-chevron-down"
                      style={{
                        position: "relative",
                        top: "1px",
                        fontSize: 16,
                      }}
                    />
                  </span>
                </Button>
              </Whisper>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default SupportalUserHeader;
